/** QuickPlan — Bibliothèque intégrée : meuble (panneaux, façades articulées, quincaillerie).
 *
 * Convention : façade du meuble côté Y = 0 (devant), profondeur vers +Y, Z vers le haut.
 * Chaque panneau a son origine au coin minimal (bas, avant, gauche).
 * Emplacements de fixation : direction = côté TÊTE (sortante), la tige part à l'opposé.
 * Prix indicatifs : mélaminé 19 mm ≈ 35 €/m², fond 3 mm ≈ 8 €/m².
 */

const MELA = '#d9c8a9'; // mélaminé chêne clair
const BLANC = '#eeeeea';
const METAL = '#9aa0a6';

const E = { key: 'E', label: 'Épaisseur', default: 19, options: [8, 10, 12, 16, 18, 19, 22, 25, 30], unit: 'mm' };
const CONFIRMAT = { type: 'vis', d: 7, l: 50, ref: 'CONF-7x50', name: 'Vis d\'assemblage (confirmat) 7×50', price: 0.12 };

const out = [
  {
    id: 'mb-cote',
    name: 'Côté (panneau vertical)',
    category: 'meuble',
    family: 'Panneaux',
    description: 'Panneau vertical, épaisseur E selon X, profondeur P selon Y, hauteur H selon Z. Origine au coin bas-avant-gauche. Emplacements : 3 vis d\'assemblage en haut et en bas (vissées depuis la face X = 0 si G = 0, depuis X = E si G = 1 — mettre G = 1 pour un côté droit).',
    material: 'Panneau mélaminé',
    color: MELA,
    params: [
      { key: 'P', label: 'Profondeur', default: 560, min: 50, max: 1200, unit: 'mm' },
      { key: 'H', label: 'Hauteur', default: 720, min: 50, max: 2800, unit: 'mm' },
      E,
      { key: 'G', label: 'Vissage par (0 = X0, 1 = XE)', default: 0, options: [0, 1] },
    ],
    parts: [{ type: 'box', size: ['E', 'P', 'H'] }],
    ports: [
      { id: 'bas-av', pos: [0, 0, 0] },
      { id: 'haut-av', pos: [0, 0, 'H'] },
      { id: 'bas-av-int', label: 'Bas avant face intérieure', pos: ['E', 0, 0] },
    ],
    fixings: [
      { ...CONFIRMAT, pos: ['G*E', '40+(i%3)*(P-80)/2', 'floor(i/3)*(H-E)+E/2'], dir: ['2*G-1', 0, 0], repeat: { count: 6, var: 'i' } },
    ],
    bom: { kind: 'piece', label: '{H}×{P}×{E}', price: 'H*P/1e6*35' },
  },
  {
    id: 'mb-tablette',
    name: 'Dessus / dessous / tablette',
    category: 'meuble',
    family: 'Panneaux',
    description: 'Panneau horizontal L (X) × P (Y), épaisseur E selon Z. Origine au coin bas-avant-gauche.',
    material: 'Panneau mélaminé',
    color: MELA,
    params: [
      { key: 'L', label: 'Largeur', default: 562, min: 50, max: 2800, unit: 'mm' },
      { key: 'P', label: 'Profondeur', default: 560, min: 50, max: 1200, unit: 'mm' },
      E,
    ],
    parts: [{ type: 'box', size: ['L', 'P', 'E'] }],
    ports: [{ id: 'av-g', pos: [0, 0, 0] }, { id: 'av-d', pos: ['L', 0, 0] }],
    bom: { kind: 'piece', label: '{L}×{P}×{E}', price: 'L*P/1e6*35' },
  },
  {
    id: 'mb-fond',
    name: 'Fond',
    category: 'meuble',
    family: 'Panneaux',
    description: 'Panneau de fond vertical L (X) × H (Z), épaisseur E selon Y (3 mm par défaut). Origine au coin bas-avant-gauche. Emplacements : pointes tous les 150 mm sur le pourtour, frappées depuis l\'arrière.',
    material: 'Panneau HDF',
    color: '#c9b892',
    params: [
      { key: 'L', label: 'Largeur', default: 562, min: 50, max: 2800, unit: 'mm' },
      { key: 'H', label: 'Hauteur', default: 682, min: 50, max: 2800, unit: 'mm' },
      { key: 'E', label: 'Épaisseur', default: 3, options: [3, 5, 8, 10, 16, 19], unit: 'mm' },
    ],
    parts: [{ type: 'box', size: ['L', 'E', 'H'] }],
    fixings: [
      // Rangées haute et basse : une pointe tous les ~150 mm.
      { type: 'clou', pos: ['20+(k%(ceil((L-40)/150)+1))*(L-40)/max(1,ceil((L-40)/150))', 'E', '10+floor(k/(ceil((L-40)/150)+1))*(H-20)'], dir: [0, 1, 0], d: 1.6, l: 20, repeat: { count: '2*(ceil((L-40)/150)+1)', var: 'k' } },
      // Chants gauche et droit (hors coins déjà pointés).
      { type: 'clou', pos: ['10+floor(k/max(1,ceil((H-40)/150)-1))*(L-20)', 'E', '20+(k%max(1,ceil((H-40)/150)-1)+1)*(H-40)/max(1,ceil((H-40)/150))'], dir: [0, 1, 0], d: 1.6, l: 20, repeat: { count: '2*max(0,ceil((H-40)/150)-1)', var: 'k' } },
    ],
    bom: { kind: 'piece', label: '{L}×{H}×{E}', price: 'L*H/1e6*(8+max(0,E-5)*2)' },
  },
  {
    id: 'mb-porte',
    name: 'Porte',
    category: 'meuble',
    family: 'Façades',
    description: 'Porte en applique L (X) × H (Z), épaisseur E selon Y, face avant à Y = 0, origine au coin bas-gauche. ARTICULÉE : charnière à gauche (C = 0) ou à droite (C = 1), ouverture 0 → 110° vers l\'avant. Emplacements : 2 vis par charnière, côté intérieur.',
    material: 'Panneau mélaminé blanc',
    color: BLANC,
    params: [
      { key: 'L', label: 'Largeur', default: 597, min: 100, max: 1200, unit: 'mm' },
      { key: 'H', label: 'Hauteur', default: 717, min: 100, max: 2600, unit: 'mm' },
      E,
      { key: 'C', label: 'Charnières (0 gauche, 1 droite)', default: 0, options: [0, 1] },
    ],
    parts: [{ type: 'box', size: ['L', 'E', 'H'] }],
    ports: [{ id: 'bas-g', pos: [0, 'E', 0] }, { id: 'centre', pos: ['L/2', 0, 'H/2'] }],
    joint: { type: 'hinge', label: 'Ouverture (°)', axis: [0, 0, '2*C-1'], origin: ['C*L', 'E', 0], min: 0, max: 110 },
    fixings: [{
      type: 'vis', d: 3.5, l: 16, ref: 'VIS-3,5x16', name: 'Vis aggloméré 3,5×16',
      pos: ['22+C*(L-44)', 'E', '100+floor(i/2)*(H-200)/(max(2,ceil(H/700))-1)+(i%2)*24-12'],
      dir: [0, 1, 0], repeat: { count: '2*max(2, ceil(H/700))', var: 'i' },
    }],
    bom: { kind: 'piece', label: '{L}×{H}×{E}', price: 'L*H/1e6*45' },
    kit: [{ ref: 'CHAR-110', name: 'Charnière invisible 110° avec embase', qty: 'max(2, ceil(H/700))', price: 2.5 }],
  },
  {
    id: 'mb-facade',
    name: 'Façade fixe',
    category: 'meuble',
    family: 'Façades',
    description: 'Façade fixe (fausse façade, bandeau) L (X) × H (Z), épaisseur E selon Y, face avant à Y = 0.',
    material: 'Panneau mélaminé blanc',
    color: BLANC,
    params: [
      { key: 'L', label: 'Largeur', default: 597, min: 30, max: 2800, unit: 'mm' },
      { key: 'H', label: 'Hauteur', default: 100, min: 20, max: 2600, unit: 'mm' },
      E,
    ],
    parts: [{ type: 'box', size: ['L', 'E', 'H'] }],
    bom: { kind: 'piece', label: '{L}×{H}×{E}', price: 'L*H/1e6*45' },
  },
  {
    id: 'mb-tiroir',
    name: 'Tiroir complet',
    category: 'meuble',
    family: 'Façades',
    description: 'Tiroir avec façade (L × H, ép. 19, face avant à Y = -19) et caisson de tiroir (côtés 16 mm, fond 8 mm) sur coulisses, vers +Y sur P. ARTICULÉ : glissière vers l\'avant, course 0 → 85 % de P.',
    material: 'Mélaminé',
    color: BLANC,
    params: [
      { key: 'L', label: 'Largeur façade', default: 597, min: 200, max: 1200, unit: 'mm' },
      { key: 'H', label: 'Hauteur façade', default: 200, min: 80, max: 500, unit: 'mm' },
      { key: 'P', label: 'Longueur de coulisse', default: 500, options: [300, 350, 400, 450, 500, 550], unit: 'mm' },
    ],
    parts: [
      { type: 'box', name: 'façade', size: ['L', 19, 'H'], pos: [0, -19, 0] },
      { type: 'box', name: 'côté g', size: [16, 'P', 'H-60'], pos: [30, 0, 30], color: MELA },
      { type: 'box', name: 'côté d', size: [16, 'P', 'H-60'], pos: ['L-46', 0, 30], color: MELA },
      { type: 'box', name: 'arrière', size: ['L-92', 16, 'H-80'], pos: [46, 'P-16', 30], color: MELA },
      { type: 'box', name: 'fond', size: ['L-92', 'P-16', 8], pos: [46, 0, 30], color: '#c9b892' },
      { type: 'box', name: 'coulisse g', size: [12, 'P', 40], pos: [18, 0, 40], color: METAL },
      { type: 'box', name: 'coulisse d', size: [12, 'P', 40], pos: ['L-30', 0, 40], color: METAL },
    ],
    ports: [{ id: 'bas-g', pos: [0, 0, 0] }, { id: 'centre', pos: ['L/2', -19, 'H/2'] }],
    joint: { type: 'slide', label: 'Sortie (mm)', axis: [0, -1, 0], min: 0, max: 'P*0.85' },
    drill: false, // tiroir livré monté : seuls ses perçages dans le caisson comptent
    fixings: [
      { type: 'vis', d: 4, l: 16, ref: 'VIS-4x16', name: 'Vis aggloméré 4×16', pos: ['24+floor(i/3)*(L-48)', '30+(i%3)*(P-60)/2', 60], dir: ['floor(i/3)*2-1', 0, 0], repeat: { count: 6, var: 'i' } },
    ],
    bom: { kind: 'piece', label: 'façade {L}×{H}, coulisse {P}', price: 'L*H/1e6*45+25' },
    kit: [{ ref: 'COUL-BILLES', name: 'Paire de coulisses à billes', qty: 1, price: 12 }],
  },
  {
    id: 'mb-poignee',
    name: 'Poignée barre',
    category: 'meuble',
    family: 'Quincaillerie',
    description: 'Poignée barre, entraxe C, horizontale (le long de X). Origine au centre sur la face avant de la façade ; la poignée dépasse vers -Y. Tourner de -90° autour de Y pour une poignée verticale. Emplacements : 2 vis M4 depuis l\'arrière de la façade (épaisseur F).',
    material: 'Inox brossé',
    color: METAL,
    params: [
      { key: 'C', label: 'Entraxe', default: 128, options: [96, 128, 160, 192, 256, 320], unit: 'mm' },
      { key: 'F', label: 'Épaisseur de façade', default: 19, min: 8, max: 40, unit: 'mm' },
    ],
    parts: [
      { type: 'cyl', name: 'barre', r: 6, h: 'C+40', axis: 'x', pos: ['-C/2-20', -32, 0] },
      { type: 'cyl', name: 'pied g', r: 5, h: 32, axis: 'y', pos: ['-C/2', -32, 0] },
      { type: 'cyl', name: 'pied d', r: 5, h: 32, axis: 'y', pos: ['C/2', -32, 0] },
    ],
    ports: [{ id: 'centre', pos: [0, 0, 0], dir: [0, -1, 0] }],
    fixings: [{ type: 'vis', d: 4, l: 'F+6', ref: 'VIS-M4', name: 'Vis M4 pour poignée', pos: ['(2*i-1)*C/2', 'F', 0], dir: [0, 1, 0], repeat: { count: 2, var: 'i' } }],
    bom: { kind: 'piece', label: 'entraxe {C}', price: 4 },
  },
  {
    id: 'mb-bouton',
    name: 'Bouton de meuble',
    category: 'meuble',
    family: 'Quincaillerie',
    description: 'Bouton Ø30. Origine au centre sur la face avant de la façade ; dépasse vers -Y.',
    material: 'Métal',
    color: METAL,
    parts: [
      { type: 'cyl', name: 'tige', r: 5, h: 18, axis: 'y', pos: [0, -18, 0] },
      { type: 'cyl', name: 'tête', r: 15, h: 10, axis: 'y', pos: [0, -28, 0] },
    ],
    ports: [{ id: 'centre', pos: [0, 0, 0] }],
    fixings: [{ type: 'vis', d: 4, l: 25, ref: 'VIS-M4x25', name: 'Vis M4×25', pos: [0, 19, 0], dir: [0, 1, 0] }],
    bom: { kind: 'piece', price: 2.5 },
  },
  {
    id: 'mb-pied',
    name: 'Pied de meuble réglable',
    category: 'meuble',
    family: 'Quincaillerie',
    description: 'Pied réglable sous caisson. Origine = dessus de la platine (sous le panneau du bas) ; descend de H. Emplacements : 4 vis dans la platine.',
    material: 'Plastique / acier',
    color: '#3a3d42',
    params: [{ key: 'H', label: 'Hauteur', default: 100, min: 60, max: 200, unit: 'mm' }],
    parts: [
      { type: 'cyl', name: 'platine', r: 25, h: 4, pos: [0, 0, -4] },
      { type: 'cyl', name: 'fût', r: 14, h: 'H-4', pos: [0, 0, '-H'] },
    ],
    ports: [{ id: 'haut', pos: [0, 0, 0] }],
    fixings: [{ type: 'vis', d: 4, l: 16, ref: 'VIS-AGGLO-4x16', name: 'Vis aggloméré 4×16', pos: ['(i%2)*25.4-12.7', 'floor(i/2)*25.4-12.7', -4], dir: [0, 0, -1], repeat: { count: 4, var: 'i' } }],
    bom: { kind: 'piece', label: 'H {H}', price: 1.8 },
  },
  {
    id: 'mb-tasseau27',
    name: 'Tasseau 27×27',
    category: 'meuble',
    family: 'Bois massif',
    description: 'Tasseau bois brut 27×27, extrudé selon X (0 → L), section centrée.',
    material: 'Sapin raboté',
    color: '#d8b77f',
    params: [{ key: 'L', label: 'Longueur', default: 1000, min: 20, max: 2400, unit: 'mm' }],
    parts: [{ type: 'extrude', section: 'tube27x27x0', length: 'L', axis: 'x' }],
    ports: [{ id: 'debut', pos: [0, 0, 0], dir: [-1, 0, 0] }, { id: 'fin', pos: ['L', 0, 0], dir: [1, 0, 0] }],
    bom: { kind: 'cut', label: '{L} mm', lengthExpr: 'L', pricePerM: 2.5, stock: 'Tasseau 27×27' },
  },
  {
    id: 'mb-chevron45',
    name: 'Chevron 45×45',
    category: 'meuble',
    family: 'Bois massif',
    description: 'Chevron bois 45×45, extrudé selon X (0 → L), section centrée.',
    material: 'Sapin raboté',
    color: '#d1ad73',
    params: [{ key: 'L', label: 'Longueur', default: 1000, min: 20, max: 4000, unit: 'mm' }],
    parts: [{ type: 'extrude', section: 'tube45x45x0', length: 'L', axis: 'x' }],
    ports: [{ id: 'debut', pos: [0, 0, 0], dir: [-1, 0, 0] }, { id: 'fin', pos: ['L', 0, 0], dir: [1, 0, 0] }],
    bom: { kind: 'cut', label: '{L} mm', lengthExpr: 'L', pricePerM: 4, stock: 'Chevron 45×45' },
  },
  {
    id: 'mb-penderie',
    name: 'Tube de penderie Ø25',
    category: 'meuble',
    family: 'Quincaillerie',
    description: 'Tube de penderie Ø25 le long de X (0 → L), axe à l\'origine. Supports compris.',
    material: 'Acier chromé',
    color: METAL,
    params: [{ key: 'L', label: 'Longueur', default: 800, min: 100, max: 2000, unit: 'mm' }],
    parts: [{ type: 'cyl', r: 12.5, h: 'L', axis: 'x', pos: [0, 0, 0] }],
    ports: [{ id: 'debut', pos: [0, 0, 0] }, { id: 'fin', pos: ['L', 0, 0] }],
    bom: { kind: 'cut', label: '{L} mm', lengthExpr: 'L', pricePerM: 6, stock: 'Tube penderie Ø25' },
    kit: [{ ref: 'SUP-PEND', name: 'Support de tube de penderie', qty: 2, price: 1.2 }],
  },
  {
    id: 'mb-taquet-palette',
    name: 'Taquet à palette (à enfoncer)',
    category: 'quincaillerie',
    family: 'Taquets',
    description: "Taquet d'étagère à enfoncer, tige Ø D et palette plate (type acier chromé Ø5). Origine sur la face du montant, sur l'axe du trou : la tige s'enfonce vers -X sur Li (perçage Ø D compté), la palette sort vers +X sur Lp. La palette s'ENCASTRE dans un logement sous la tablette (encoche usinée, comptée dans les plans de perçage) : la tablette ne glisse plus. Poser le dessous de la tablette sur le port « tablette ». Montant à droite (matière côté +X) : rot [0, 0, 180].",
    ref: 'TAQ-PAL',
    material: 'Acier chromé',
    color: '#c4c9cf',
    params: [
      { key: 'D', label: 'Ø tige', default: 5, options: [3, 4, 5, 6, 8], unit: 'mm' },
      { key: 'Li', label: 'Longueur enfoncée', default: 8, min: 4, max: 20, unit: 'mm' },
      { key: 'Lp', label: 'Longueur de palette', default: 12, min: 6, max: 30, unit: 'mm' },
      { key: 'B', label: 'Largeur de palette', default: 8, min: 4, max: 20, unit: 'mm' },
      { key: 'Ep', label: 'Épaisseur de palette', default: 2.5, min: 1, max: 6, unit: 'mm' },
      { key: 'J', label: 'Jeu du logement', default: 0.5, min: 0, max: 2, unit: 'mm' },
    ],
    parts: [
      { type: 'cyl', name: 'tige', r: 'D/2', h: 'Li+1', pos: ['-Li', 0, 0], axis: 'x' },
      { type: 'box', name: 'palette', size: ['Lp', 'B', 'Ep'], pos: [1, '-B/2', '-Ep/2'] },
    ],
    ports: [
      { id: 'tablette', label: 'Dessous de tablette (palette encastrée)', pos: [0, 0, '-Ep/2'], dir: [0, 0, -1] },
      { id: 'trou', label: 'Axe du trou, face du montant', pos: [0, 0, 0], dir: [1, 0, 0] },
    ],
    fixings: [{ type: 'percage', pos: [0, 0, 0], dir: [1, 0, 0], d: 'D', l: 'Li' }],
    recesses: [{ name: 'logement de palette', pos: [0, '-B/2-J', '-Ep/2'], size: ['Lp+1+J', 'B+2*J', 'Ep+J'] }],
    bom: { kind: 'piece', label: 'Ø{D}, palette {Lp}×{B}', price: 0.1 },
  },
  {
    id: 'mb-ligne-taquets',
    name: 'Ligne de trous à taquets',
    category: 'quincaillerie',
    family: 'Taquets',
    description: "Ligne verticale de N trous Ø D, profondeur P, au pas « pas » (réglage en hauteur des tablettes). Origine sur la face du montant au trou le plus bas ; les trous s'enfoncent vers -X, la ligne monte vers +Z. Perçages comptés dans les plans de perçage, rien en nomenclature. Montant à droite : rot [0, 0, 180].",
    color: '#2b2b2b',
    params: [
      { key: 'N', label: 'Nombre de trous', default: 20, min: 2, max: 80, step: 1 },
      { key: 'pas', label: 'Pas vertical', default: 32, min: 16, max: 100, unit: 'mm' },
      { key: 'D', label: 'Ø', default: 5, options: [3, 4, 5, 6, 8], unit: 'mm' },
      { key: 'P', label: 'Profondeur', default: 10, min: 4, max: 20, unit: 'mm' },
    ],
    parts: [{ type: 'cyl', r: 'D/2', h: 0.6, pos: [-0.5, 0, 'i*pas'], axis: 'x', repeat: { count: 'N', var: 'i' } }],
    ports: [{ id: 'bas', label: 'Trou le plus bas', pos: [0, 0, 0], dir: [1, 0, 0] }],
    fixings: [{ type: 'percage', pos: [0, 0, 'i*pas'], dir: [1, 0, 0], d: 'D', l: 'P', repeat: { count: 'N', var: 'i' } }],
    bom: { kind: 'none', label: 'Ø{D} × {N}, pas {pas}' },
  },
];

export default out;
